import React, { useContext } from 'react';
import { Mail, CheckCircle2, X, Info } from 'lucide-react';
import { UserContext } from '../context/user/UserContext';
const EmailVerificationModal = ({ isOpen, onClose, onResend, loading }) => {
  const { user } = useContext(UserContext);
  const [isBusy, setIsBusy] = React.useState(false);
  const [timeLeft, setTimeLeft] = React.useState(0);

  const attemptsLeft = 5 - (user?.verification_email_count || 0);
  const lastAttempt = user?.last_verification_attempt
    ? new Date(user.last_verification_attempt)
    : null;

  React.useEffect(() => {
    if (!lastAttempt || attemptsLeft > 0) {
      setTimeLeft(0);
      return;
    }

    const calculateTimeLeft = () => {
      const now = new Date();
      const oneHourLater = new Date(lastAttempt.getTime() + 60 * 60 * 1000);
      const diff = oneHourLater - now;
      return Math.max(0, Math.floor(diff / 1000));
    };

    const initial = calculateTimeLeft();
    setTimeLeft(initial);

    if (initial <= 0) return;

    const timer = setInterval(() => {
      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);
      if (remaining <= 0) clearInterval(timer);
    }, 1000);

    return () => clearInterval(timer);
  }, [lastAttempt, attemptsLeft, isOpen]);

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) {
      return `${h}h ${m}m ${s}s`;
    }
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const [busyMessage, setBusyMessage] = React.useState('');
  const [busyTitle, setBusyTitle] = React.useState('Email Service Busy');

  React.useEffect(() => {
    const handleBusy = (e) => {
      if (e.detail?.message) setBusyMessage(e.detail.message);
      if (e.detail?.title) setBusyTitle(e.detail.title);
      setIsBusy(true);
    };
    window.addEventListener('email-service-busy', handleBusy);
    return () => window.removeEventListener('email-service-busy', handleBusy);
  }, []);

  React.useEffect(() => {
    if (!isOpen) {
      setIsBusy(false);
      setBusyMessage('');
      setBusyTitle('Email Service Busy');
    }
  }, [isOpen]);

  const handleCloseClick = () => {
    setIsBusy(false);
    onClose();
  };

  const handleResendClick = async () => {
    try {
      setIsBusy(false);
      await onResend();
    } catch (err) {
      console.log(err.message, 'err.message from backend');
      const isLimitError = err?.message
        ?.toLowerCase()
        .includes('limit of verification email requests');

      setBusyTitle(
        isLimitError ? 'Request Limit Reached' : 'Email Service Busy'
      );
      setBusyMessage(
        err.message ||
          'We are facing some issues sending the verification email right now. Please try again later.'
      );
      setIsBusy(true);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
        onClick={handleCloseClick}
      />

      {/* Modal Content */}
      <div className="relative w-full max-w-md transform overflow-hidden rounded-3xl bg-white p-6 sm:p-8 shadow-2xl transition-all border border-slate-100">
        {/* Close Button */}
        <button
          onClick={handleCloseClick}
          className="absolute right-4 top-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors"
        >
          <X size={20} />
        </button>

        <div className="flex flex-col items-center text-center">
          {isBusy ? (
            <>
              {/* Busy Icon Header */}
              <div className="mb-4 sm:mb-6 relative">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-orange-50 rounded-2xl sm:rounded-3xl flex items-center justify-center text-orange-500 animate-pulse">
                  <Mail
                    size={32}
                    className="sm:w-10 sm:h-10"
                    strokeWidth={1.5}
                  />
                </div>
                <div className="absolute -bottom-1 -right-1 bg-white p-1 rounded-full shadow-sm">
                  <X
                    className="text-orange-500 bg-white rounded-full"
                    size={20}
                  />
                </div>
              </div>

              <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 sm:mb-3 tracking-tight">
                {busyTitle}
              </h3>
              
              {busyTitle.includes('Limit') ? (
                <div className="w-full space-y-4">
                  <div className="flex flex-col items-center gap-2 sm:gap-3 p-4 sm:p-6 bg-slate-50 rounded-2xl border border-slate-100 shadow-inner w-full">
                    <div className="flex items-center gap-2 text-rose-500">
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-rose-500 animate-ping" />
                      <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider">
                        Wait Period Active
                      </span>
                    </div>
                    <div className="text-3xl sm:text-4xl font-black text-slate-800 tracking-tight tabular-nums">
                      {timeLeft > 0 ? formatTime(timeLeft) : "0:00"}
                    </div>
                    <p className="text-[10px] sm:text-xs text-slate-400 font-medium text-center px-2 sm:px-4 leading-relaxed">
                      {busyMessage || "You've reached the limit of verification email requests. Please wait for the timer to expire."}
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    className="w-full py-3.5 px-6 bg-rose-500 hover:bg-rose-600 active:bg-rose-700 text-white font-bold rounded-xl shadow-lg shadow-rose-200 transition-all flex items-center justify-center gap-2"
                  >
                    Okay, I'll wait
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-6 sm:mb-8">
                    {busyMessage || (
                      <>
                        We are facing some issues sending the verification email
                        right now. Please try again later. Don't worry, your account
                        is safe and you can verify it later.
                      </>
                    )}
                  </p>

                  <button
                    onClick={onClose}
                    className="w-full py-3.5 px-6 bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white font-bold rounded-xl shadow-lg shadow-orange-200 transition-all flex items-center justify-center gap-2"
                  >
                    Okay, I'll wait
                  </button>
                </>
              )}
            </>
          ) : (
            <>
              {/* Normal Icon Header */}
              <div className="mb-4 sm:mb-6 relative">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-rose-50 rounded-2xl sm:rounded-3xl flex items-center justify-center text-rose-500 animate-pulse">
                  <Mail
                    size={32}
                    className="sm:w-10 sm:h-10"
                    strokeWidth={1.5}
                  />
                </div>
                <div className="absolute -bottom-1 -right-1 bg-white p-1 rounded-full shadow-sm">
                  <CheckCircle2
                    className="text-rose-500"
                    size={20}
                    fill="white"
                  />
                </div>
              </div>

              <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 sm:mb-3 tracking-tight">
                Verify Your Email
              </h3>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-6 sm:mb-8">
                To ensure the security of our community, we require email
                verification for actions like creating posts and managing
                projects. Please check your inbox for the verification link.
              </p>

              {/* Actions */}
              <div className="w-full space-y-4">
                {attemptsLeft <= 0 && timeLeft > 0 ? (
                  <div className="flex flex-col items-center gap-2 sm:gap-3 p-4 sm:p-6 bg-slate-50 rounded-2xl border border-slate-100 shadow-inner">
                    <div className="flex items-center gap-2 text-rose-500">
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-rose-500 animate-ping" />
                      <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider">
                        Limit Reached
                      </span>
                    </div>
                    <div className="text-3xl sm:text-4xl font-black text-slate-800 tracking-tight tabular-nums">
                      {formatTime(timeLeft)}
                    </div>
                    <p className="text-[10px] sm:text-xs text-slate-400 font-medium text-center px-2 sm:px-4 leading-relaxed">
                      You've used all verification attempts. You can request
                      another email once the timer above expires.
                    </p>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={handleResendClick}
                      disabled={loading}
                      className="w-full py-3.5 px-6 bg-rose-500 hover:bg-rose-600 active:bg-rose-700 text-white font-bold rounded-xl shadow-lg shadow-rose-200 transition-all disabled:opacity-70 disabled:grayscale disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          <span>Sending Link...</span>
                        </>
                      ) : (
                        'Resend Verification Link'
                      )}
                    </button>

                    <div className="flex items-center justify-center gap-2 py-1 px-3 bg-slate-50 rounded-lg w-fit mx-auto border border-slate-100">
                      <div
                        className={`w-1.5 h-1.5 rounded-full ${attemptsLeft <= 1 ? 'bg-rose-500 animate-pulse' : 'bg-rose-400'}`}
                      />
                      <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                        {attemptsLeft > 0
                          ? `${attemptsLeft} Attempt${attemptsLeft !== 1 ? 's' : ''} Left`
                          : 'No Attempts Remaining'}
                      </span>
                    </div>
                  </>
                )}

                <button
                  onClick={onClose}
                  className="w-full py-3 px-6 bg-white hover:bg-slate-50 text-slate-600 font-semibold rounded-xl border border-slate-200 transition-colors"
                >
                  I'll do it later
                </button>
              </div>
            </>
          )}

          <p className="mt-6 text-xs text-slate-400 uppercase tracking-widest font-bold">
            Handpreneur Security
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationModal;
